import { shopifyProductField } from '@/core/contentfulConfig';
import { TypeProductFields } from '@/core/generated-types';
import { getProductById } from '../shopify';
import { getContentType } from '@/core/constants';
import { Entry, EntryCollection } from 'contentful';

const productFieldName = shopifyProductField();

async function appendShopifyProduct(entry: Entry<any>) {
  const contentType = getContentType(entry);
  if (contentType === 'product' && !entry.fields.shopifyProduct) {
    const productFields = entry.fields;
    const productId =
      productFields[productFieldName as keyof TypeProductFields];
    if (productId) {
      const shopifyProduct = await getProductById(productId);
      entry.fields.shopifyProduct = shopifyProduct;
    }
  }
}

async function checkNextLevel(entry: Entry<any>) {
  if (!entry || !entry.fields) return;

  // Iterate through all fields in the entry
  for (const value of Object.values(entry.fields)) {
    if (!value || typeof value !== 'object') continue;

    if (Array.isArray(value)) {
      // Handle arrays of entries
      await Promise.all(
        value.map((item) => {
          if (
            item &&
            typeof item === 'object' &&
            'sys' in item &&
            'fields' in item
          ) {
            return fetchShopifyProduct(item as Entry<any>);
          }

          return Promise.resolve();
        }),
      );
    } else if ('sys' in value && 'fields' in value) {
      // Handle single entry
      await fetchShopifyProduct(value as Entry<any>);
    }
  }
}

export async function fetchShopifyProduct(
  entry: Entry<any> | undefined | null,
) {
  if (!entry || typeof entry !== 'object') return;

  if (!('fields' in entry)) return;

  // Only try to append Shopify product if this is a proper Contentful entry
  if ('sys' in entry) {
    await appendShopifyProduct(entry);
  }

  // Always traverse nested fields to find product entries
  await checkNextLevel(entry);
}

export default async function fetchShopifyProducts<T extends object>(
  entries: EntryCollection<T>,
) {
  const fetchPromises = entries.items.map((entry) =>
    fetchShopifyProduct(entry),
  );

  await Promise.all(fetchPromises);

  return entries;
}
